import { DownloadOutlined } from "@ant-design/icons";
import { Button, Col, DatePicker, Row, Select, Typography } from "antd";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { getOverview, getRangeStats } from "../../api/dashboard.api";
import Card from "../../components/ui/Card";
import LineChart from "../../components/ui/LineChart";
import Spinner from "../../components/ui/Spinner";
import StatCard from "../../components/ui/StatCard";
import { useDashboard } from "../../hooks/useDashboard";
import { formatCost, formatMs } from "../../utils/formatters";

const INTENT_LABELS: Record<string, string> = {
  support_ticket: "Intent to Create Ticket",
  faq: "FAQ",
  greetings: "Greetings",
};

const DEFAULT_RANGE: [Dayjs, Dayjs] = [
  dayjs().subtract(30, "day").startOf("day"),
  dayjs().endOf("day"),
];

// ── CSV download helper ─────────────────────────────────────────────────────
function downloadRangeCsv(rangeData: any, start: Dayjs, end: Dayjs) {
  const rows = [
    ["Metric", "Value"],
    ["Period", `${start.format("MMM D, YYYY")} – ${end.format("MMM D, YYYY")}`],
    ["Sessions", rangeData.total_sessions],
    ["Messages", rangeData.total_messages],
    ["Avg Latency", formatMs(rangeData.avg_latency_ms)],
    ["Total Cost", formatCost(rangeData.total_cost)],
    ["Tickets Created", rangeData.tickets_created],
    ["Tracking Inquiries", rangeData.tracking_count],
    ...Object.entries(rangeData.intent_counts ?? {}).map(([k, v]) => [
      INTENT_LABELS[k] ?? k,
      v,
    ]),
  ];
  const csv = rows
    .map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(","))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `overview-${start.format("YYYYMMDD")}-${end.format("YYYYMMDD")}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Component ───────────────────────────────────────────────────────────────
const OverviewPage = () => {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>(DEFAULT_RANGE);
  const [searchNums, setSearchNums] = useState<string[]>([]);

  const tnParam = searchNums.length ? searchNums.join(",") : undefined;

  const { data, loading } = useDashboard(
    () => getOverview({ tracking_numbers: tnParam }),
    [tnParam]
  );

  const { data: rangeData, loading: rangeLoading } = useDashboard(
    () =>
      getRangeStats({
        start_date: dateRange[0].toISOString(),
        end_date: dateRange[1].toISOString(),
        tracking_numbers: tnParam,
      }),
    [dateRange, tnParam]
  );

  if (loading || !data) return <Spinner />;

  const goToDetails = () => {
    navigate(
      `/tracking-details?start=${dateRange[0].toISOString()}&end=${dateRange[1].toISOString()}`
    );
  };

  return (
    <div className="page-stack">
      {/* ── Tracking number search ── */}
      <Card>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <Typography.Text strong>Filter by Tracking Numbers:</Typography.Text>
          <Select
            mode="tags"
            style={{ flex: 1, minWidth: 280 }}
            placeholder="Type tracking numbers and press Enter…"
            value={searchNums}
            onChange={setSearchNums}
            tokenSeparators={[",", " "]}
            open={false}
          />
          {searchNums.length > 0 && (
            <Button onClick={() => setSearchNums([])}>Clear</Button>
          )}
        </div>
      </Card>

      {/* ── Range stats ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          flexWrap: "wrap",
          marginTop: 8,
        }}
      >
        <Typography.Title level={4} style={{ margin: 0 }}>
          Range Stats
        </Typography.Title>
        <DatePicker.RangePicker
          value={dateRange}
          onChange={range => {
            if (range?.[0] && range?.[1]) {
              setDateRange([range[0].startOf("day"), range[1].endOf("day")]);
            }
          }}
        />
        {rangeData && (
          <Button
            icon={<DownloadOutlined />}
            onClick={() =>
              downloadRangeCsv(rangeData, dateRange[0], dateRange[1])
            }
          >
            Download Report
          </Button>
        )}
      </div>

      {rangeLoading || !rangeData ? (
        <Spinner />
      ) : (
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12} xl={6}>
            <StatCard
              title="Sessions"
              value={String(rangeData.total_sessions)}
            />
          </Col>
          <Col xs={24} md={12} xl={6}>
            <StatCard
              title="Messages"
              value={String(rangeData.total_messages)}
            />
          </Col>
          <Col xs={24} md={12} xl={6}>
            <StatCard
              title="Avg Latency"
              value={formatMs(rangeData.avg_latency_ms)}
            />
          </Col>
          <Col xs={24} md={12} xl={6}>
            <StatCard
              title="Total Cost"
              value={formatCost(rangeData.total_cost)}
            />
          </Col>
          <Col xs={24} md={12} xl={6}>
            <StatCard
              title="Tickets Created"
              value={String(rangeData.tickets_created)}
            />
          </Col>
          <Col xs={24} md={12} xl={6}>
            <div
              style={{ cursor: "pointer" }}
              onClick={() => goToDetails()}
              onKeyDown={e => e.key === "Enter" && goToDetails()}
              role="button"
              tabIndex={0}
              title="Click to see details"
            >
              <StatCard
                title="Tracking Number Count"
                value={String(rangeData.tracking_count)}
                helper="Click to view details →"
              />
            </div>
          </Col>
          {Object.entries(rangeData.intent_counts ?? {})
            .filter(([intent]) => intent !== "package_tracking")
            .map(([intent, count]) => (
              <Col key={intent} xs={24} md={12} xl={6}>
                <StatCard
                  title={INTENT_LABELS[intent] ?? intent}
                  value={String(count)}
                />
              </Col>
            ))}
        </Row>
      )}

      {/* ── Charts (below range stats) ── */}
      <Card title="Messages over 30 days">
        <LineChart
          data={data.messages_over_time}
          series={[
            { dataKey: "count", name: "Messages", color: "var(--accent)" },
          ]}
        />
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={12}>
          <Card title="Cost over time">
            <LineChart
              data={data.cost_over_time}
              series={[
                {
                  dataKey: "cost",
                  name: "Cost",
                  color: "var(--accent-secondary)",
                },
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} xl={12}>
          <Card title="Latency over time">
            <LineChart
              data={data.latency_over_time}
              series={[
                { dataKey: "avg_ms", name: "Latency", color: "var(--accent)" },
              ]}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default OverviewPage;
